// web/src/components/PublishProgress.tsx — the publish wait, made legible (22_PREVIEW_AND_PUBLISH.md §3/§4).
//
// Publishing merges dev→main and Amplify rebuilds production; that takes ~3–5 minutes and cannot
// be removed (01 §5 — these are SSR apps, so there is no artifact to promote). §2's decision is to
// make the wait honest rather than hide it.
//
// Two rules this component exists to keep:
//   1. The bar CAPS AT 95% and stays there until the poller flips the row to `live`. 100% followed
//      by continued waiting is the single most trust-destroying progress pattern there is (§8).
//   2. Past 2× the expected time the copy stops counting down and says so, rather than sitting at
//      95% in silence.
// Neither the branch, the merge sha, nor Amplify is named anywhere in here (§3).
import { useEffect, useState } from 'react';
import { useReducedMotion } from 'motion/react';

/** §3: "default 210 s until five exist" — mirrors DEFAULT_PRODUCTION_BUILD_MS in the API, and is
 *  only ever reached when the server sent no ETA at all (a pre-0016 response). */
const DEFAULT_EXPECTED_MS = 210_000;
const CAP_PCT = 95;

export function PublishProgress({
  startedAt,
  expectedMs,
}: {
  /** unix SECONDS (the contract's timestamp unit), not millis. */
  startedAt: number | null | undefined;
  expectedMs: number | null | undefined;
}) {
  const reduce = useReducedMotion();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const expected = expectedMs && expectedMs > 0 ? expectedMs : DEFAULT_EXPECTED_MS;
  // A missing/absurd start time must not produce a negative bar or a "-3 minutes left" — treat it
  // as "just started" and let the copy carry the honesty instead.
  const started = startedAt ? startedAt * 1000 : now;
  const elapsed = Math.max(0, now - started);
  const pct = Math.min((elapsed / expected) * CAP_PCT, CAP_PCT);
  const late = elapsed > expected * 2;
  const minutesLeft = Math.max(Math.ceil((expected - elapsed) / 60_000), 1);

  const message = late
    ? 'Taking longer than usual — still working.'
    : `About ${minutesLeft} minute${minutesLeft === 1 ? '' : 's'} left.`;

  return (
    <section className="mt-5 rounded-lg border border-hair border-l-4 border-l-run bg-surface p-4">
      <h2 className="mb-2 font-head text-sm text-muted">Publishing your update</h2>
      <p className="text-sm text-ink">
        We're putting this live on your site now. You can close this page — it'll keep going.
      </p>
      {/* Progress is never colour-only: the bar is paired with the copy below it, and the whole
          thing is a live region so a screen reader hears the countdown change. */}
      <div
        className="mt-3 h-2 overflow-hidden rounded-pill bg-surface2"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
        aria-label="Publishing your update"
      >
        <div
          className="h-full rounded-pill bg-run"
          style={{ width: `${pct}%`, transition: reduce ? 'none' : 'width 1s linear' }}
        />
      </div>
      <p className="mt-2 text-xs text-muted" aria-live="polite">
        {message}
      </p>
    </section>
  );
}
